// 🐛 Bug reports v1 (09-20). One modal lives at the app level
// (components/BugReportModal, mounted in App.js); anything anywhere opens it
// with openBugReport(). Two doors today: `/bug` in the composer and the
// "Report a bug" button in the What's New pill.
//
// The report rides the interjection route as body.bug_report (the backend's
// CFN-cap piggyback, handler.js) — no new endpoint. The server whitelists the
// context key by key, so nothing extra put here is ever stored.
//
// ⚠️ NEVER put a player secret, a recovery code or an API key in the context.
// The secret rides the BODY root only, where the server validates it and
// never stores it.
import { API_BASE_URL } from '../config/api';

export const BUG_REPORT_EVENT = 'spaces-open-bug-report';
export const BUG_TEXT_MIN = 5;
export const BUG_TEXT_MAX = 2000;

// ── recent client errors: a tiny ring buffer, installed once at import ──
const MAX_ERRORS = 8;
const recentErrors = [];
const pushError = (msg, src) => {
  const m = String(msg || '').slice(0, 300);
  if (!m) return;
  recentErrors.push({ at: new Date().toISOString(), msg: m, src: String(src || '').slice(0, 200) });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
};
let installed = false;
export function installBugReportErrorTap() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => pushError(e?.message, e?.filename ? `${e.filename}:${e.lineno || 0}` : ''));
  window.addEventListener('unhandledrejection', (e) => pushError(e?.reason?.message || String(e?.reason || ''), 'promise'));
}
export const getRecentErrors = () => recentErrors.slice();
export const _pushErrorForTests = pushError;

// Spaces register what only the page knows (room, last action ids). A getter,
// not a snapshot: it is read at SUBMIT time, so the context is what the
// reporter was looking at when they hit send.
let pageContextGetter = null;
export function setBugPageContext(getter) { pageContextGetter = typeof getter === 'function' ? getter : null; }

/** Open the modal. detail: { text?, source? } */
export function openBugReport(detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BUG_REPORT_EVENT, { detail }));
}

let buildHashPromise = null;
const getBuildHash = () => {
  if (!buildHashPromise) {
    buildHashPromise = fetch('/build-info.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j && (j.git || j.gitHash || j.git_hash || j.commit || j.hash)) || null)
      .catch(() => null);
  }
  return buildHashPromise;
};

export async function buildBugContext(source) {
  let page = {};
  try { page = (pageContextGetter && pageContextGetter()) || {}; } catch (e) { page = {}; }
  const build = await getBuildHash();
  return {
    url: window.location.href,
    route: window.location.pathname,
    build: build ? String(build) : null,
    room: page.room || null,
    game_type: page.game_type || null,
    ws: page.ws || null,
    source: source || null,
    user_agent: navigator.userAgent,
    lang: navigator.language,
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
    errors: getRecentErrors(),
    last_action_ids: Array.isArray(page.last_action_ids) ? page.last_action_ids.slice(-12) : [],
  };
}

/** POST one report. Throws an Error with a user-ready message on failure. */
export async function submitBugReport({ text, contact, source }) {
  let page = {};
  try { page = (pageContextGetter && pageContextGetter()) || {}; } catch (e) { page = {}; }
  const gameId = page.game_id || new URLSearchParams(window.location.search).get('gameId') || null;
  const body = {
    bug_report: { text: String(text || '').trim(), contact: contact ? String(contact).trim() : null, context: await buildBugContext(source) },
  };
  if (gameId) body.game_id = gameId;
  if (page.player_id) {
    body.player_id = page.player_id;
    if (page.player_secret) body.player_secret = page.player_secret;
  }
  const response = await fetch(`${API_BASE_URL}/chat-game/interjection`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || !json.success) {
    throw new Error(json.error || 'That did not send. Try again in a moment.');
  }
  return json;
}
